import memoizeOne from "memoize-one";
import { useContext } from "react";
import { useParams } from "react-router";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";
import { useRouteProps } from "utilities/url/url";

import { PortalInfoContext } from "commonComponents/utilities/InlineOwnerPreview/PortalInfoContext";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { type InvoiceFormat } from "handlers";

import { type IOwnerInvoiceProps } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice";
import { InlinePreviewOwnerInvoiceHandler } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.api.handler";
import {
    type IReconciliationLocationState,
    type OwnerInvoiceEntity,
} from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.api.types";

const OwnerInvoice = lazyLoadComponent(() => import("./OwnerInvoice"));

const PreviewOwnerInvoiceHandler = memoizeOne((ownerInvoice: OwnerInvoiceEntity) => {
    return new InlinePreviewOwnerInvoiceHandler(ownerInvoice);
});

const OwnerInvoicePage = (
    props: Omit<
        IOwnerInvoiceProps,
        "id" | "jobId" | "history" | "location" | "match" | "isPayOnline" | "invoiceFormat"
    >,
) => {
    const builderInfo = useContext(BuilderInfoContext);
    const portalInfo = useContext(PortalInfoContext);
    const params = useParams<{
        id: string;
        jobId: string;
        payOnline?: string;
        invoiceFormat?: string;
    }>();
    const routeProps = useRouteProps();

    let handler;
    if (portalInfo.currentPortal !== portalInfo.rootPortal) {
        handler = PreviewOwnerInvoiceHandler(props.ownerInvoice!);
    }

    // Grid flow carries the wizard output in location state; the change-order flow passes a prop,
    // which takes precedence.
    const locationLineItemsToInvoice = (
        routeProps.location.state as IReconciliationLocationState | undefined
    )?.lineItemsToInvoice;

    return (
        <OwnerInvoice
            {...props}
            {...routeProps}
            lineItemsToInvoice={props.lineItemsToInvoice ?? locationLineItemsToInvoice}
            modalConfig={{
                parentRoute: props.modalConfig ? props.modalConfig.parentRoute : "",
                beforeClose: () => {
                    props.onClose?.(routeProps);
                    props.modalConfig?.beforeClose();
                },
            }}
            id={Number(params.id)}
            jobId={Number(params.jobId)}
            isPayOnline={params.payOnline === "true"}
            builderInfo={builderInfo}
            ownerInvoiceHandler={handler}
            invoiceFormat={
                params.invoiceFormat !== undefined
                    ? (Number(params.invoiceFormat) as InvoiceFormat)
                    : undefined
            }
        />
    );
};

export const RouteOwnerInvoice = (
    props: Omit<
        IOwnerInvoiceProps,
        "id" | "jobId" | "history" | "location" | "match" | "isPayOnline" | "invoiceFormat"
    >,
) => (
    <RouteRelative path={routes.ownerInvoice.details}>
        <OwnerInvoicePage {...props} />
    </RouteRelative>
);
